import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Award, Plus, Check, X, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BONUS_TYPES = [
  { value: 'referral', label: 'Referral bonus' },
  { value: 'sales', label: 'Sales bonus' },
  { value: 'performance', label: 'Performance bonus' },
  { value: 'on_call', label: 'On-call bonus' },
  { value: 'after_hours', label: 'After-hours completed call bonus' },
  { value: 'review', label: 'Customer review bonus' },
  { value: 'helper', label: 'Helper bonus' },
  { value: 'manual_adjustment', label: 'Manual adjustment' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
];

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Saturday-start pay period for the given ISO date string (YYYY-MM-DD)
const saturdayPayPeriod = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d.getTime())) return '';
  const dow = d.getDay(); // 0=Sun..6=Sat
  // Days to go back to reach Saturday: Sat=0,Sun=1,Mon=2,...Fri=6
  const back = (dow + 1) % 7;
  d.setDate(d.getDate() - back);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const labelForBonusType = (t) => BONUS_TYPES.find((b) => b.value === t)?.label || (t ? t.replace(/_/g, ' ') : '—');
const labelForStatus = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || (s || '—');

const AdminBonusRequests = () => {
  const { user, hasPermission } = useAuth();
  const isOwnerTier = ['super_admin', 'admin'].includes(user?.role);
  // iter160 — Split bonus perms (was a single `approve_bonus_requests` check).
  // Owner tier still gets everything; managers/office_staff get only what's granted.
  const canApprove   = isOwnerTier || hasPermission?.('approve_bonus_requests');
  const canCreate    = isOwnerTier || hasPermission?.('create_bonus_for_employee') || hasPermission?.('approve_bonus_requests');
  const canMarkPaid  = isOwnerTier || hasPermission?.('mark_bonus_paid');
  // Legacy alias used by row/manage controls.
  const canManage    = canApprove;

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal state for Add Bonus
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    employee_id: '',
    amount: '',
    bonus_type: 'performance',
    customer_name: '',
    invoice_id: '',
    appointment_id: '',
    bonus_date: todayISO(),
    pay_period: saturdayPayPeriod(todayISO()),
    reason: '',
    status: isOwnerTier ? 'approved' : 'pending',
  });

  // Pending-row controls (approve flow)
  const [finals, setFinals] = useState({});
  const [notes, setNotes] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (employeeFilter && employeeFilter !== 'all') params.employee_id = employeeFilter;
      if (startDate) params.start = startDate;
      if (endDate) params.end = endDate;
      const { data } = await axios.get(`${API}/admin/bonus-requests`, { params, withCredentials: true });
      setItems(data || []);
      const f = {};
      (data || []).forEach((b) => {
        const amt = b.final_amount ?? b.proposed_amount ?? b.amount;
        if (amt != null) f[b.id] = String(amt);
      });
      setFinals(f);
    } catch (err) {
      toast.error('Failed to load bonuses');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, employeeFilter, startDate, endDate]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/employees`, { withCredentials: true });
      setEmployees((data || []).filter((e) => e.role !== 'customer'));
    } catch (err) {
      // non-fatal
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const onChangeBonusDate = (val) => {
    setForm((f) => ({ ...f, bonus_date: val, pay_period: saturdayPayPeriod(val) }));
  };

  const resetForm = () => {
    setForm({
      employee_id: '',
      amount: '',
      bonus_type: 'performance',
      customer_name: '',
      invoice_id: '',
      appointment_id: '',
      bonus_date: todayISO(),
      pay_period: saturdayPayPeriod(todayISO()),
      reason: '',
      status: isOwnerTier ? 'approved' : 'pending',
    });
  };

  const submitAdd = async () => {
    if (!form.employee_id) { toast.error('Pick an employee'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a bonus amount'); return; }
    if (!form.bonus_type) { toast.error('Pick a bonus type'); return; }
    if (!form.reason || !form.reason.trim()) { toast.error('Add a short reason/notes'); return; }
    setSubmitting(true);
    try {
      const payload = {
        employee_id: form.employee_id,
        amount: Number(form.amount),
        bonus_type: form.bonus_type,
        appointment_id: form.appointment_id || null,
        invoice_id: form.invoice_id || null,
        customer_name: form.customer_name || '',
        bonus_date: form.bonus_date || null,
        pay_period: form.pay_period || '',
        reason: form.reason.trim(),
        status: form.status || (isOwnerTier ? 'approved' : 'pending'),
      };
      await axios.post(`${API}/admin/bonus-requests`, payload, { withCredentials: true });
      toast.success('Bonus added');
      setShowAdd(false);
      resetForm();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add bonus');
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (id, status) => {
    try {
      const body = { status, admin_notes: notes[id] || '' };
      if (status === 'approved') body.final_amount = Number(finals[id] || 0);
      await axios.put(`${API}/admin/bonus-requests/${id}`, body, { withCredentials: true });
      toast.success(`Bonus ${status}`);
      refresh();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const markPaid = async (b) => {
    try {
      const pay_period = b.pay_period || saturdayPayPeriod(b.bonus_date || todayISO());
      await axios.post(
        `${API}/admin/bonus-requests/${b.id}/mark-paid`,
        { pay_period, payment_method: 'payroll', notes: '' },
        { withCredentials: true },
      );
      toast.success('Marked paid');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark paid');
    }
  };

  const statusPill = (s) => {
    const base = 'text-xs uppercase font-bold px-2.5 py-1 rounded';
    if (s === 'approved') return `${base} text-green-400 bg-green-500/10`;
    if (s === 'rejected') return `${base} text-red-400 bg-red-500/10`;
    if (s === 'paid') return `${base} text-blue-400 bg-blue-500/10`;
    return `${base} text-amber-300 bg-amber-500/10`;
  };

  const totalShown = useMemo(() => items.reduce((sum, b) => sum + Number(b.final_amount ?? b.proposed_amount ?? b.amount ?? 0), 0), [items]);

  return (
    <div data-testid="admin-bonus-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2">
            <Award className="w-6 h-6 text-[#D4AF37]" /> Bonus Requests
          </h2>
          <p className="text-[#A3A3A3]">Employee-submitted requests and admin-added bonuses</p>
        </div>
        {canCreate && (
          <Button
            data-testid="add-bonus-btn"
            onClick={() => { resetForm(); setShowAdd(true); }}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-semibold"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Bonus
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-2 flex-wrap">
          {['pending', 'approved', 'rejected', 'paid', ''].map((s) => (
            <Button
              data-testid={`filter-${s || 'all'}`}
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              variant={statusFilter === s ? 'default' : 'outline'}
              className={statusFilter === s ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]' : 'border-[#262626] text-white hover:bg-[#262626]'}
            >
              {(s || 'all').toUpperCase()}
            </Button>
          ))}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[#A3A3A3] text-xs">Employee</span>
          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger data-testid="filter-employee" className="w-56 bg-[#0A0A0A] border-[#262626] text-white">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white">
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name || e.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[#A3A3A3] text-xs">From</span>
          <Input data-testid="filter-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[#A3A3A3] text-xs">To</span>
          <Input data-testid="filter-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-40" />
        </div>
        {(startDate || endDate || employeeFilter !== 'all') && (
          <Button
            data-testid="clear-filters"
            variant="outline"
            onClick={() => { setStartDate(''); setEndDate(''); setEmployeeFilter('all'); }}
            className="border-[#262626] text-white hover:bg-[#262626]"
          >
            Clear
          </Button>
        )}
        <div className="ml-auto text-sm text-[#A3A3A3]">
          {items.length} record{items.length === 1 ? '' : 's'} · Total ${totalShown.toFixed(2)}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#A3A3A3]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Date</th>
                <th className="text-left px-3 py-2 font-semibold">Employee</th>
                <th className="text-right px-3 py-2 font-semibold">Amount</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-left px-3 py-2 font-semibold">Related</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold">Submitted / Added by</th>
                <th className="text-left px-3 py-2 font-semibold">Notes</th>
                <th className="text-left px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#A3A3A3]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#A3A3A3]">No bonuses found</td></tr>
              ) : items.map((b) => {
                const date = b.bonus_date || (b.created_at ? new Date(b.created_at).toLocaleDateString() : '—');
                const amt = b.final_amount ?? b.proposed_amount ?? b.amount ?? 0;
                const isAdminCreated = b.source === 'admin';
                const submittedBy = isAdminCreated
                  ? (b.created_by_name ? `${b.created_by_name} (admin)` : 'Admin')
                  : (b.user_name || 'Employee');
                const related = b.customer_name || (b.invoice_id ? `Inv #${String(b.invoice_id).slice(-6)}` : b.appointment_id ? `Job ${String(b.appointment_id).slice(-6)}` : '—');
                return (
                  <tr key={b.id} className="border-t border-[#262626] hover:bg-[#0F0F0F]" data-testid={`bonus-row-${b.id}`}>
                    <td className="px-3 py-2 text-white whitespace-nowrap">{date}</td>
                    <td className="px-3 py-2 text-white">{b.user_name || '—'}</td>
                    <td className="px-3 py-2 text-right text-white font-semibold">${Number(amt).toFixed(2)}</td>
                    <td className="px-3 py-2 text-[#E5E5E5]">{labelForBonusType(b.bonus_type)}</td>
                    <td className="px-3 py-2 text-[#E5E5E5]">{related}</td>
                    <td className="px-3 py-2"><span className={statusPill(b.status)}>{labelForStatus(b.status)}</span></td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{submittedBy}</td>
                    <td className="px-3 py-2 text-[#A3A3A3] max-w-[280px]"><div className="truncate" title={b.reason || b.notes || ''}>{b.reason || b.notes || '—'}</div></td>
                    <td className="px-3 py-2">
                      {canApprove && b.status === 'pending' && (
                        <div className="flex flex-col gap-1 min-w-[200px]">
                          <Input data-testid={`final-${b.id}`} type="number" placeholder="Final $" value={finals[b.id] || ''} onChange={(e) => setFinals({ ...finals, [b.id]: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-8" />
                          <Input placeholder="Admin notes (optional)" value={notes[b.id] || ''} onChange={(e) => setNotes({ ...notes, [b.id]: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-8" />
                          <div className="flex gap-1">
                            <Button data-testid={`approve-${b.id}`} onClick={() => decide(b.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white h-8 px-2 flex-1"><Check className="w-3 h-3 mr-1" />Approve</Button>
                            <Button data-testid={`reject-${b.id}`} onClick={() => decide(b.id, 'rejected')} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white h-8 px-2 flex-1"><X className="w-3 h-3 mr-1" />Reject</Button>
                          </div>
                        </div>
                      )}
                      {canMarkPaid && b.status === 'approved' && (
                        <Button data-testid={`paid-${b.id}`} onClick={() => markPaid(b)} className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-2">
                          <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Bonus Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-bonus-dialog" className="bg-[#0A0A0A] border-[#262626] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Add Bonus</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="text-[#A3A3A3]">Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="form-employee" className="bg-[#141414] border-[#262626] text-white"><SelectValue placeholder="Select employee…" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} <span className="text-[#A3A3A3]">({e.role || 'employee'})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Amount ($) *</Label>
              <Input data-testid="form-amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Bonus type *</Label>
              <Select value={form.bonus_type} onValueChange={(v) => setForm({ ...form, bonus_type: v })}>
                <SelectTrigger data-testid="form-type" className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white">
                  {BONUS_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Customer (optional)</Label>
              <Input data-testid="form-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Invoice # (optional)</Label>
              <Input data-testid="form-invoice" value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} placeholder="e.g. inv id" className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Bonus date</Label>
              <Input data-testid="form-bonus-date" type="date" value={form.bonus_date} onChange={(e) => onChangeBonusDate(e.target.value)} className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-[#A3A3A3]">Pay period (Sat start)</Label>
              <Input data-testid="form-pay-period" type="date" value={form.pay_period} onChange={(e) => setForm({ ...form, pay_period: e.target.value })} className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#A3A3A3]">Reason / notes *</Label>
              <Textarea data-testid="form-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. After-hours completed call for Smith residence" rows={3} className="bg-[#141414] border-[#262626] text-white" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#A3A3A3]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="form-status" className="bg-[#141414] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#262626] text-white">
                  {STATUS_OPTIONS.filter((o) => isOwnerTier || o.value === 'pending' || o.value === 'rejected').map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isOwnerTier && (
                <p className="text-xs text-[#A3A3A3] mt-1">Managers can only submit bonuses as pending or rejected. Super Admin can approve, mark paid, or reject.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            <Button data-testid="form-submit" onClick={submitAdd} disabled={submitting} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-semibold">
              {submitting ? 'Adding…' : 'Add Bonus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBonusRequests;
